import { useCallback, useMemo, useRef, useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  LABEL_CONFIG_AREAS,
  LABEL_CONFIG_DEPENDENT_TYPES,
  ORDER_STATUS,
  PLATFORM_MODULES,
  TIME_UNITS,
} from "@miplatform/constants";
import { useComponentState, useDebounce, useRouter } from "@miplatform/hooks";
import {
  areSameDayOrBothNull,
  buildSearchQuery,
  endOf,
  getDate,
  startOf,
} from "@miplatform/utils";
import {
  ACCOUNTING_SORT_OPTIONS,
  ACCOUNTING_TABS,
  ACCOUNTING_TABS_NAMES,
} from "./accountingConstants";

export const useAccountingViewModel = () => {
  const { params, navigate } = useRouter();
  const { tab } = params;

  const getTabFromUrl = (urlTab) => {
    const tabMap = {
      all: ACCOUNTING_TABS_NAMES.ALL,
      "pre-bill": ACCOUNTING_TABS_NAMES.PRE_BILL,
      "pending-invoices": ACCOUNTING_TABS_NAMES.PENDING_INVOICES,
      invoiced: ACCOUNTING_TABS_NAMES.INVOICED,
    };
    return tabMap[urlTab] || ACCOUNTING_TABS_NAMES.ALL;
  };

  const getInitialTab = () => {
    if (tab) {
      const tabValue = getTabFromUrl(tab);
      return (
        ACCOUNTING_TABS.find(({ value }) => value === tabValue) ||
        ACCOUNTING_TABS[0]
      );
    }
    return ACCOUNTING_TABS[0];
  };

  const initialTab = getInitialTab();
  const [activeTab, setActiveTab] = useState(initialTab);

  const getDefaultDateRangeForTabValue = (tabValue) => {
    const isOptionalDateRangeTab =
      tabValue === ACCOUNTING_TABS_NAMES.PRE_BILL ||
      tabValue === ACCOUNTING_TABS_NAMES.PENDING_INVOICES;

    if (isOptionalDateRangeTab) {
      return { startDate: null, endDate: null };
    }

    const today = getDate();
    return { startDate: today, endDate: today };
  };

  const dateRangeRef = useRef(getDefaultDateRangeForTabValue(initialTab.value));

  const [tabs] = useState(ACCOUNTING_TABS);
  const [orderTemplates, setOrderTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    selectedOrderTemplate: null,
    dateRange: { ...dateRangeRef.current },
  });

  const todayStartLocal = useMemo(
    () => startOf(TIME_UNITS.DAY, getDate()).toDate(),
    []
  );
  const [refresh, setRefresh] = useState(false);
  const [orderHoverCardConfig, setOrderHoverCardConfig] = useState(null);
  const [orderCardConfig, setOrderCardConfig] = useState(null);
  const [orderTypeCount, setOrderTypeCount] = useState({});
  const debouncedSearch = useDebounce(search, 500);
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const getUrlFromTab = (tabValue) => {
    const urlMap = {
      [ACCOUNTING_TABS_NAMES.ALL]: "all",
      [ACCOUNTING_TABS_NAMES.PRE_BILL]: "pre-bill",
      [ACCOUNTING_TABS_NAMES.PENDING_INVOICES]: "pending-invoices",
      [ACCOUNTING_TABS_NAMES.INVOICED]: "invoiced",
    };
    return urlMap[tabValue] || "all";
  };

  const handleTabChange = (tabValue) => {
    const newTab = tabs.find(({ value }) => value === tabValue);
    if (newTab) {
      const defaultRange = getDefaultDateRangeForTabValue(newTab.value);
      dateRangeRef.current = defaultRange;
      setFilters((prev) => ({ ...prev, dateRange: defaultRange }));

      setActiveTab(newTab);
      const urlTab = getUrlFromTab(tabValue);
      navigate(`/accounting/invoices/${urlTab}`);
    }
  };

  const handleResetDateRange = useCallback(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dateRange: { ...dateRangeRef.current },
    }));
  }, [setFilters]);

  const isDateRangeDisabled = () => {
    return (
      areSameDayOrBothNull(
        filters.dateRange.startDate,
        dateRangeRef.current.startDate
      ) &&
      areSameDayOrBothNull(
        filters.dateRange.endDate,
        dateRangeRef.current.endDate
      )
    );
  };

  const handleFilterChange = useCallback(
    ({ orderTemplateId, orderTemplateLabel, dateRange }) => {
      const invalidSelectedDate = Boolean(
        dateRange &&
        ((dateRange.startDate && !dateRange.startDate.isValid?.()) ||
          (dateRange.endDate && !dateRange.endDate.isValid?.()))
      );

      setFilters((prevFilters) => ({
        ...prevFilters,
        ...(orderTemplateId !== undefined && {
          selectedOrderTemplate:
            orderTemplateId === null ||
              prevFilters.selectedOrderTemplate?.id === orderTemplateId
              ? null
              : { id: orderTemplateId, label: orderTemplateLabel },
        }),
        ...(dateRange && {
          dateRange: invalidSelectedDate
            ? { startDate: null, endDate: null }
            : {
              startDate:
                dateRange.startDate !== undefined
                  ? dateRange.startDate
                  : prevFilters.dateRange.startDate,
              endDate:
                dateRange.endDate !== undefined
                  ? dateRange.endDate
                  : prevFilters.dateRange.endDate,
            },
        }),
      }));
    },
    [setFilters]
  );

  const handleRefresh = useCallback(
    () => setRefresh((prev) => !prev),
    [setRefresh]
  );

  const searchQuery = buildSearchQuery(search, [
    "home/address/addressLine1",
    "home/communityName",
    "home/builderName",
    "home/address/city",
  ]);

  const commonFilters = ({ top, skip, excludeOrderTemplate } = {}) => {
    const hasSearchQuery = Object.keys(searchQuery || {}).length > 0;

    if (activeTab.value === ACCOUNTING_TABS_NAMES.PRE_BILL) {
      const approvedStartDateField = activeTab.dateRange;

      const basePrebillFilter = {
        or: [
          { orderTemplateModule: { eq: PLATFORM_MODULES.MI_SCHEDULING } },
          {
            and: [
              { orderTemplateModule: { eq: PLATFORM_MODULES.MI_DISPATCH } },
              {
                or: [
                  { [approvedStartDateField]: { le: todayStartLocal } },
                  { orderStatus: { eq: ORDER_STATUS.COMPLETE } },
                ],
              },
            ],
          },
        ],
      };

      const filterParts = [basePrebillFilter];

      if (filters.selectedOrderTemplate && !excludeOrderTemplate) {
        filterParts.push({
          orderTemplateId: {
            eq: { type: "guid", value: filters.selectedOrderTemplate.id },
          },
        });
      }

      if (activeTab.status) {
        filterParts.push({ billingStatus: { in: activeTab.status } });
      }

      if (hasSearchQuery) {
        filterParts.push(searchQuery);
      }

      if (filters.dateRange.startDate && filters.dateRange.endDate) {
        filterParts.push({
          [approvedStartDateField]: {
            ge: startOf(TIME_UNITS.DAY, filters.dateRange.startDate).toDate(),
            le: endOf(TIME_UNITS.DAY, filters.dateRange.endDate).toDate(),
          },
        });
      }

      const combinedFilter =
        filterParts.length === 1 ? filterParts[0] : { and: filterParts };

      return {
        sort: activeTab.sort || ACCOUNTING_SORT_OPTIONS.RECEIVED_DATE,
        top,
        skip,
        filters: combinedFilter,
      };
    }

    const baseFilters = {
      ...(filters.selectedOrderTemplate &&
        !excludeOrderTemplate && {
        orderTemplateId: {
          eq: { type: "guid", value: filters.selectedOrderTemplate.id },
        },
      }),
      ...(activeTab.status && {
        billingStatus: {
          in: activeTab.status,
        },
      }),
      ...searchQuery,
    };

    const dateRangeFilters =
      activeTab.dateRange &&
        filters.dateRange.startDate &&
        filters.dateRange.endDate
        ? {
          [activeTab.dateRange]: {
            ge: startOf(TIME_UNITS.DAY, filters.dateRange.startDate).toDate(),
            le: endOf(TIME_UNITS.DAY, filters.dateRange.endDate).toDate(),
          },
        }
        : {};

    return {
      sort: activeTab.sort || ACCOUNTING_SORT_OPTIONS.RECEIVED_DATE,
      top,
      skip,
      filters: { ...baseFilters, ...dateRangeFilters },
    };
  };

  const getOrderTemplates = ({ top = 25, skip } = {}) => {
    return stateManager.run(async () =>
      taskService.order.getAll(commonFilters({ top, skip }))
    );
  };

  const getOrderCardConfig = () => {
    stateManager.run(async () => {
      const response = await taskService.labelConfig.get({
        dependentType: LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
        area: LABEL_CONFIG_AREAS.CARD,
        isActive: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderCardConfig(response.data);
      }
    });
    stateManager.run(async () => {
      const response = await taskService.labelConfig.get({
        dependentType: LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
        area: LABEL_CONFIG_AREAS.HOVER_CARD,
        isActive: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderHoverCardConfig(response.data);
      }
    });
  };

  const getOrderTypesCount = () => {
    stateManager.run(async () => {
      const response = await taskService.order.getOrderTemplateSummary({
        ...commonFilters({ excludeOrderTemplate: true }),
        params: {
          groupBy: "BillingStatus",
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTypeCount(
          response.data.reduce((acc, orderType) => {
            acc[orderType.id] = orderType.count;
            return acc;
          }, {})
        );
      }
    });
  };

  const getOrderActivity = async (orderId) => {
    return stateManager.run(async () => {
      const response = await taskService.activities.getAll({
        params: {
          refId: `task-service/orders/${orderId}`,
        },
      });
      return response;
    });
  };

  const getInvoices = async ({ orderId }) => {
    const response = await accountService.invoices.getAll({
      filters: {
        orderId: {
          eq: { type: "guid", value: orderId },
        },
      },
      sort: "createdOn",
      enableCaching: true,
    });
    return response.status === HTTP_STATUSES.OK ? response.data : null;
  };

  return {
    activeTab,
    orderCardConfig,
    orderHoverCardConfig,
    orderTypeCount,
    filters,
    tabs,
    refresh,
    orderTemplates,
    isLoading,
    stateManager,
    search,
    debouncedSearch,
    getOrderActivity,
    setOrderTemplates,
    isDateRangeDisabled,
    handleTabChange,
    setSearch,
    setFilters,
    handleFilterChange,
    handleResetDateRange,
    handleRefresh,
    getOrderCardConfig,
    getOrderTemplates,
    getOrderTypesCount,
    getInvoices,
  };
};
